import { useSyncExternalStore } from "react";
import type {
  BridgePersona,
  PersonaSummary,
  PersonaChoice,
  ThemeId,
  AspirationId,
  TimeBudgetId,
  ObstacleId,
  BridgeState,
  DreamPlan,
  DossierChapterData,
  ReflectionAnswers,
  ReflectionControlAnswer,
  ReflectionAreaId,
  ReflectionAreaAnswer,
} from "./types";
import { emptyBridgeState, ENGINE_VERSION, sanitizeFreeText } from "./state";
import {
  loadSaved,
  saveProgress,
  clearSaved,
  isConsentGiven,
  setConsent as storeSetConsent,
} from "./storage";
import { buildInterpretation } from "./interpret";
import { generatePlan } from "./plan";
import { ASPIRATIONS, MAX_THEMES, MAX_ASPIRATIONS, MAX_OBSTACLES, MAX_WEEKLY_REFLECTIONS, WEEKLY_REFLECTION_MAX } from "./options";

/**
 * Internal module-level store state shape.
 */
export interface BridgeStoreState {
  personas: PersonaSummary[];
  requestedPersonaIndex: number | null;
  requestToken: number;
  bridge: BridgeState;
  plan: DreamPlan | null;
  dossierChapter: DossierChapterData | null;
  /** In-memory ONLY sensitive reflection answers: never persisted, never in link/PDF */
  reflection: ReflectionAnswers;
}

// Module-level state holder
let state: BridgeStoreState = {
  personas: [],
  requestedPersonaIndex: null,
  requestToken: 0,
  bridge: loadSaved() ?? emptyBridgeState(),
  plan: null,
  dossierChapter: null,
  reflection: {},
};

// Listeners set
const listeners = new Set<() => void>();

function notify() {
  for (const listener of listeners) {
    try {
      listener();
    } catch {
      // Ignore listener error
    }
  }
}

/**
 * Resolve active BridgePersona from current choice & available personas.
 */
function resolveCurrentPersona(
  choice: PersonaChoice | null,
  personas: PersonaSummary[]
): BridgePersona {
  if (choice && choice.kind === "self") {
    const found = personas.find((p) => p.index === choice.index);
    if (found) {
      return {
        kind: "self",
        archetype: found.archetype,
        name: found.name,
        title: found.title,
        occupation: found.occupation,
        scenarioLabel: found.scenarioLabel,
        topAxis: found.topAxis,
      };
    }
  }
  return { kind: "own" };
}

/**
 * Re-derive Plan and DossierChapter from current state.
 */
function recomputeDerived(currentState: BridgeStoreState): {
  plan: DreamPlan | null;
  dossierChapter: DossierChapterData | null;
} {
  const { bridge, personas } = currentState;
  const { aspirations, themes, interpretationDecision, rewordedText, timeBudget, obstacles } = bridge;

  // Plan requires approved interpretation, aspirations, and a time budget
  if (
    aspirations.length === 0 ||
    (interpretationDecision !== "accepted" && interpretationDecision !== "reworded") ||
    !timeBudget
  ) {
    return { plan: null, dossierChapter: null };
  }

  const persona = resolveCurrentPersona(bridge.personaChoice, personas);

  // Interpretation text
  let approvedInterpretation = "";
  if (interpretationDecision === "accepted") {
    const interp = buildInterpretation({ persona, themes, aspirations });
    approvedInterpretation = interp.text;
  } else if (interpretationDecision === "reworded") {
    approvedInterpretation = rewordedText.trim();
  }

  if (!approvedInterpretation) {
    return { plan: null, dossierChapter: null };
  }

  const plan = generatePlan({
    persona,
    themes,
    aspirations,
    approvedInterpretation,
    timeBudget,
    obstacles: obstacles.length > 0 ? obstacles : undefined,
  });

  let dossierChapter: DossierChapterData | null = null;
  if (plan && bridge.includeInDossier) {
    const personaLabel =
      persona.kind === "self"
        ? `${persona.name} (${persona.scenarioLabel} – ${persona.occupation})`
        : "Your Own Imagined Persona";

    const aspirationsLabels = aspirations
      .map((a) => ASPIRATIONS.find((opt) => opt.id === a)?.label)
      .filter(Boolean) as string[];

    dossierChapter = {
      engineVersion: ENGINE_VERSION,
      personaLabel,
      aspirationsLabels,
      approvedInterpretation,
      plan,
    };
  }

  return { plan, dossierChapter };
}

/**
 * Mutate state helper: updates state, re-derives, saves to storage (if consented), and notifies.
 */
function updateState(fn: (prev: BridgeStoreState) => Partial<BridgeStoreState>) {
  const partial = fn(state);
  const nextBridge = partial.bridge ?? state.bridge;
  const nextPersonas = partial.personas ?? state.personas;

  // Check if persona choice became invalid
  let adjustedBridge = nextBridge;
  if (nextBridge.personaChoice && nextBridge.personaChoice.kind === "self") {
    const valid = nextPersonas.some((p) => p.index === (nextBridge.personaChoice as { index: number }).index);
    if (!valid && nextPersonas.length > 0) {
      adjustedBridge = {
        ...nextBridge,
        personaChoice: { kind: "own" },
      };
    }
  }

  const intermediateState: BridgeStoreState = {
    ...state,
    ...partial,
    bridge: adjustedBridge,
    personas: nextPersonas,
  };

  const { plan, dossierChapter } = recomputeDerived(intermediateState);

  // Sync planId to progress if changed
  let finalBridge = adjustedBridge;
  if (plan && plan.planId !== adjustedBridge.progress.planId) {
    finalBridge = {
      ...adjustedBridge,
      progress: {
        planId: plan.planId,
        daysDone: [false, false, false, false, false, false, false],
        goalSteps: [
          [false, false, false],
          [false, false, false],
          [false, false, false],
        ],
      },
    };
  }

  state = {
    ...intermediateState,
    bridge: finalBridge,
    plan,
    dossierChapter,
  };

  // Persist bridge state if consent was given
  saveProgress(state.bridge);

  notify();
}

// Initial derived computation
const initialDerived = recomputeDerived(state);
state.plan = initialDerived.plan;
state.dossierChapter = initialDerived.dossierChapter;

/**
 * Actions object for interacting with the Dream Bridge.
 */
export const bridgeActions = {
  /**
   * Published by the Selves Studio when alternate selves are generated or updated.
   * Summaries contain ONLY free-sample fields.
   */
  publishPersonas(personas: PersonaSummary[]) {
    updateState((prev) => ({
      personas,
    }));
  },

  /**
   * Clear published personas when selves are reset.
   */
  clearPersonas() {
    updateState(() => ({
      personas: [],
      requestedPersonaIndex: null,
    }));
  },

  /**
   * Triggered by "Bridge this self" buttons in the studio to request selection and scroll.
   */
  requestPersona(index: 0 | 1 | 2 | 3) {
    updateState((prev) => ({
      requestedPersonaIndex: index,
      requestToken: prev.requestToken + 1,
      bridge: {
        ...prev.bridge,
        personaChoice: { kind: "self", index },
      },
    }));
  },

  /**
   * Set persona choice explicitly (own or self N).
   */
  setPersonaChoice(choice: PersonaChoice | null) {
    updateState((prev) => ({
      bridge: {
        ...prev.bridge,
        personaChoice: choice,
      },
    }));
  },

  /**
   * Set free-text imagined life description.
   * NOTE: Never read by plan generator or PDF; purely personal reflection sandbox.
   * Only strips non-printable control characters and clamps length so typing spaces is preserved.
   */
  setFreeText(text: string) {
    const raw = typeof text === "string" ? text : "";
    const cleaned = raw.replace(/[\x00-\x08\x0B\x0C\x0E-\x1F\x7F]/g, "");
    const clamped = cleaned.length > 600 ? cleaned.slice(0, 600) : cleaned;
    updateState((prev) => ({
      bridge: {
        ...prev.bridge,
        freeText: clamped,
      },
    }));
  },

  /**
   * Toggle a theme (up to MAX_THEMES = 3).
   */
  toggleTheme(theme: ThemeId) {
    updateState((prev) => {
      const exists = prev.bridge.themes.includes(theme);
      let nextThemes: ThemeId[];
      if (exists) {
        nextThemes = prev.bridge.themes.filter((t) => t !== theme);
      } else {
        if (prev.bridge.themes.length >= MAX_THEMES) {
          return {};
        }
        nextThemes = [...prev.bridge.themes, theme];
      }
      return {
        bridge: {
          ...prev.bridge,
          themes: nextThemes,
          // Re-evaluate interpretation if themes change
          interpretationDecision: "pending",
        },
      };
    });
  },

  /**
   * Toggle an aspiration area (up to MAX_ASPIRATIONS = 3).
   */
  toggleAspiration(aspiration: AspirationId) {
    updateState((prev) => {
      const exists = prev.bridge.aspirations.includes(aspiration);
      let nextAspirations: AspirationId[];
      if (exists) {
        nextAspirations = prev.bridge.aspirations.filter((a) => a !== aspiration);
      } else {
        if (prev.bridge.aspirations.length >= MAX_ASPIRATIONS) {
          return {};
        }
        nextAspirations = [...prev.bridge.aspirations, aspiration];
      }
      return {
        bridge: {
          ...prev.bridge,
          aspirations: nextAspirations,
          interpretationDecision: "pending",
        },
      };
    });
  },

  /**
   * Decide on interpretation: accept, reword, or reject.
   */
  decideInterpretation(
    decision: "pending" | "accepted" | "reworded" | "rejected",
    rewordedText?: string
  ) {
    updateState((prev) => ({
      bridge: {
        ...prev.bridge,
        interpretationDecision: decision,
        rewordedText: rewordedText !== undefined ? sanitizeFreeText(rewordedText, 400) : prev.bridge.rewordedText,
      },
    }));
  },

  /**
   * Set time budget.
   */
  setTimeBudget(budget: TimeBudgetId | null) {
    updateState((prev) => ({
      bridge: {
        ...prev.bridge,
        timeBudget: budget,
      },
    }));
  },

  /**
   * Toggle an obstacle (up to MAX_OBSTACLES = 3).
   */
  toggleObstacle(obstacle: ObstacleId) {
    updateState((prev) => {
      const exists = prev.bridge.obstacles.includes(obstacle);
      let nextObstacles: ObstacleId[];
      if (exists) {
        nextObstacles = prev.bridge.obstacles.filter((o) => o !== obstacle);
      } else {
        if (prev.bridge.obstacles.length >= MAX_OBSTACLES) {
          return {};
        }
        nextObstacles = [...prev.bridge.obstacles, obstacle];
      }
      return {
        bridge: {
          ...prev.bridge,
          obstacles: nextObstacles,
        },
      };
    });
  },

  /**
   * Toggle progress on one of the 7 daily steps (0..6).
   */
  toggleDayDone(dayIndex: number) {
    if (dayIndex < 0 || dayIndex > 6) return;
    updateState((prev) => {
      const days = [...prev.bridge.progress.daysDone] as [
        boolean,
        boolean,
        boolean,
        boolean,
        boolean,
        boolean,
        boolean,
      ];
      days[dayIndex] = !days[dayIndex];
      return {
        bridge: {
          ...prev.bridge,
          progress: {
            ...prev.bridge.progress,
            daysDone: days,
          },
        },
      };
    });
  },

  /**
   * Toggle progress on one of the 3 check-steps for goal 0..2.
   */
  toggleGoalStep(goalIndex: number, stepIndex: number) {
    if (goalIndex < 0 || goalIndex > 2 || stepIndex < 0 || stepIndex > 2) return;
    updateState((prev) => {
      const goalSteps = [
        [...prev.bridge.progress.goalSteps[0]],
        [...prev.bridge.progress.goalSteps[1]],
        [...prev.bridge.progress.goalSteps[2]],
      ] as [
        [boolean, boolean, boolean],
        [boolean, boolean, boolean],
        [boolean, boolean, boolean],
      ];
      goalSteps[goalIndex][stepIndex] = !goalSteps[goalIndex][stepIndex];
      return {
        bridge: {
          ...prev.bridge,
          progress: {
            ...prev.bridge.progress,
            goalSteps,
          },
        },
      };
    });
  },

  /**
   * Add a weekly reflection entry (max 8).
   */
  addWeeklyReflection(text: string, weekLabel?: string) {
    updateState((prev) => {
      if (prev.bridge.weeklyReflections.length >= MAX_WEEKLY_REFLECTIONS) return {};
      const label = weekLabel || `Week ${prev.bridge.weeklyReflections.length + 1}`;
      const sanitized = sanitizeFreeText(text, WEEKLY_REFLECTION_MAX);
      if (!sanitized) return {};
      return {
        bridge: {
          ...prev.bridge,
          weeklyReflections: [
            ...prev.bridge.weeklyReflections,
            { weekLabel: label, text: sanitized },
          ],
        },
      };
    });
  },

  /**
   * Remove a weekly reflection entry.
   */
  removeWeeklyReflection(index: number) {
    updateState((prev) => {
      if (index < 0 || index >= prev.bridge.weeklyReflections.length) return {};
      return {
        bridge: {
          ...prev.bridge,
          weeklyReflections: prev.bridge.weeklyReflections.filter((_, i) => i !== index),
        },
      };
    });
  },

  /**
   * Include or exclude plan from the purchased PDF dossier.
   */
  setIncludeInDossier(include: boolean) {
    updateState((prev) => ({
      bridge: {
        ...prev.bridge,
        includeInDossier: include,
      },
    }));
  },

  /**
   * Update sensitive reflection answer in memory (NEVER persisted, NEVER in plan/PDF).
   */
  setReflectionControl(answer: ReflectionControlAnswer | null) {
    state = {
      ...state,
      reflection: {
        ...state.reflection,
        control: answer,
      },
    };
    notify();
  },

  /**
   * Update sensitive reflection area answer in memory.
   */
  setReflectionArea(area: ReflectionAreaId, answer: ReflectionAreaAnswer) {
    state = {
      ...state,
      reflection: {
        ...state.reflection,
        areas: {
          ...state.reflection.areas,
          [area]: answer,
        },
      },
    };
    notify();
  },

  /**
   * Clear in-memory sensitive reflection answers.
   */
  clearReflection() {
    state = {
      ...state,
      reflection: {},
    };
    notify();
  },

  /**
   * Set storage consent.
   */
  setConsent(consent: boolean) {
    storeSetConsent(consent);
    if (consent) {
      saveProgress(state.bridge);
    }
  },

  /**
   * Check if consent is active.
   */
  isConsentGiven() {
    return isConsentGiven();
  },

  /**
   * Reset all: clears memory and saved data, keeps consent switch off.
   */
  resetAll() {
    clearSaved();
    state = {
      personas: [],
      requestedPersonaIndex: null,
      requestToken: 0,
      bridge: emptyBridgeState(),
      plan: null,
      dossierChapter: null,
      reflection: {},
    };
    notify();
  },
};

/**
 * Non-React accessor for the Dossier PDF generator.
 */
export function getDossierChapter(): DossierChapterData | null {
  return state.dossierChapter;
}

/**
 * React hook using useSyncExternalStore for granular state selection.
 */
export function useBridgeStore<T>(selector: (state: BridgeStoreState) => T): T {
  return useSyncExternalStore(
    (callback) => {
      listeners.add(callback);
      return () => {
        listeners.delete(callback);
      };
    },
    () => selector(state),
    () => selector(state)
  );
}
